/* @skip-file-for-translation */
import { z } from 'zod';
import {
  BuildingSizeUnit,
  BuildingLeaseType,
  BuildingEditState,
  BuildingPeriodKind,
  BuildingUtilityType,
} from '@watershed/constants/buildings';
import { msg, t } from '@lingui/core/macro';
import {
  GQBuildingWaterUseKind,
  GQCleanPowerContractualInstrument,
  GQIngestionCustomFieldType,
  GQStationaryCombustionFuelKind,
} from '@watershed/shared-universal/generated/graphql-schema-types';
import { CanonicalDatasetKind } from '@watershed/constants/datasets';

import {
  GQBuildingFormSupportingDocumentFragment,
  GQOrgUnitTypeWithOrgUnitsFragment,
} from '@watershed/shared-universal/generated/graphql';

import { YearMonth, YMInterval } from '../utils/YearMonth';
import assertNever from '@watershed/shared-util/assertNever';
import { formatNumber } from '../utils/helpers';
import isIncludedIn from '../utils/isIncludedIn';
import isNotNullish from '@watershed/shared-util/isNotNullish';
import must from '../utils/must';
import nullifyEmptyString from '../utils/nullifyEmptyString';
import { PartialNull } from '../utils/utilTypes';
import { UNIT_NAMES } from './BuildingsConstants';
import { getZodEnumValuesFromEnum } from '../utils/zod';

import { I18n, MessageDescriptor } from '@lingui/core';
import { i18n } from '@watershed/intl';
import { BiSupportedMeasure } from '../bi/types';

/**
 * Some building-related enums
 */

export const BuildingCleanPowerStatus = {
  FullCleanPower: 'FullCleanPower',
  NoCleanPower: 'NoCleanPower',
  PartialCleanPower: 'PartialCleanPower',
  Unknown: 'Unknown',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- make this behave like an enum
export type BuildingCleanPowerStatus =
  (typeof BuildingCleanPowerStatus)[keyof typeof BuildingCleanPowerStatus];

/**
 * Copied directly from `building_kinds_h1_2023.json`. That can be imported
 * without types (https://github.com/microsoft/TypeScript/issues/32063). We
 * could also add a codegen step to create a true `.ts` file to import from.
 */
export const BUILDING_KINDS_H1_2023 = [
  '5+ Unit Building',
  'Apartment Unit',
  'Commercial - Uncategorized',
  'Convenience store',
  'Convenience store with gas station',
  'Data Center - Colocated',
  'Data Center - Non-Colocated',
  'Education - College or university',
  'Education - Elementary or middle school',
  'Education - High school',
  'Education - Other classroom',
  'Education - Preschool or daycare',
  'Education - Uncategorized',
  'Food Sales',
  'Food Service - Fast food',
  'Food Service - Restaurant or cafeteria',
  'Food Service - Uncategorized',
  'Grocery store or food market',
  'Health Care - Inpatient',
  'Health Care - Outpatient Clinic',
  'Health Care - Outpatient Uncategorized',
  'Health Care - Uncategorized',
  'Industrial',
  'Laboratory',
  'Lodging - Dormitory or fraternity/sorority',
  'Lodging - Hotel',
  'Lodging - Motel or inn',
  'Manufactured Home',
  'Manufacturing Facility',
  'Mixed Use - Commercial and Residential',
  'Mixed Use - Predominantly Commercial',
  'Mixed Use - Predominantly Residential',
  'Multifamily - Condominiums',
  'Multifamily - Uncategorized',
  'Nursing Home',
  'Office - Administrative or Professional',
  'Office - Bank or other financial',
  'Office - Government',
  'Office - Medical non diagnostic',
  'Office - Mixed use',
  'Office - Uncategorized',
  'Parking Garage',
  'Public Assembly - Arena',
  'Public Assembly - Drama theater',
  'Public Assembly - Large Hall',
  'Public Assembly - Library',
  'Public Assembly - Movie Theater',
  'Public Assembly - Pool',
  'Public Assembly - Recreation',
  'Public Assembly - Stadium',
  'Public Assembly - Uncategorized',
  'Public Safety - Courthouse',
  'Public Safety - Fire or police station',
  'Public Safety - Penitentiary',
  'Public Safety - Uncategorized',
  'Religious worship',
  'Residential - Unknown',
  'Retail - Big Box (> 50K sf)',
  'Retail - Enclosed mall',
  'Retail - Small Box (< 50K sf)',
  'Retail - Strip shopping mall',
  'Retail - Uncategorized',
  'Retail - Vehicle dealership/showroom',
  'Service - Repair shop',
  'Service - Uncategorized',
  'Service - Vehicle service/repair shop',
  'Single Family - Attached',
  'Single Family - Detached',
  'Single Family - Uncategorized',
  'Transportation Terminal',
  'Warehouse - Distribution or Shipping center',
  'Warehouse - Non-refrigerated',
  'Warehouse - Refrigerated',
  'Warehouse - Self-storage',
  'Warehouse - Uncategorized',
] as const;

export const BUILDING_KIND_H1_2023_DISPLAY_LABELS: Record<
  (typeof BUILDING_KINDS_H1_2023)[number],
  MessageDescriptor
> = {
  '5+ Unit Building': msg({
    message: '5+ Unit Building',
    context: 'Building kind label',
  }),
  'Apartment Unit': msg({
    message: 'Apartment Unit',
    context: 'Building kind label',
  }),
  'Commercial - Uncategorized': msg({
    message: 'Commercial - Uncategorized',
    context: 'Building kind label',
  }),
  'Convenience store': msg({
    message: 'Convenience store',
    context: 'Building kind label',
  }),
  'Convenience store with gas station': msg({
    message: 'Convenience store with gas station',
    context: 'Building kind label',
  }),
  'Data Center - Colocated': msg({
    message: 'Data Center - Colocated',
    context: 'Building kind label',
  }),
  'Data Center - Non-Colocated': msg({
    message: 'Data Center - Non-Colocated',
    context: 'Building kind label',
  }),
  'Education - College or university': msg({
    message: 'Education - College or university',
    context: 'Building kind label',
  }),
  'Education - Elementary or middle school': msg({
    message: 'Education - Elementary or middle school',
    context: 'Building kind label',
  }),
  'Education - High school': msg({
    message: 'Education - High school',
    context: 'Building kind label',
  }),
  'Education - Other classroom': msg({
    message: 'Education - Other classroom',
    context: 'Building kind label',
  }),
  'Education - Preschool or daycare': msg({
    message: 'Education - Preschool or daycare',
    context: 'Building kind label',
  }),
  'Education - Uncategorized': msg({
    message: 'Education - Uncategorized',
    context: 'Building kind label',
  }),
  'Food Sales': msg({ message: 'Food Sales', context: 'Building kind label' }),
  'Food Service - Fast food': msg({
    message: 'Food Service - Fast food',
    context: 'Building kind label',
  }),
  'Food Service - Restaurant or cafeteria': msg({
    message: 'Food Service - Restaurant or cafeteria',
    context: 'Building kind label',
  }),
  'Food Service - Uncategorized': msg({
    message: 'Food Service - Uncategorized',
    context: 'Building kind label',
  }),
  'Grocery store or food market': msg({
    message: 'Grocery store or food market',
    context: 'Building kind label',
  }),
  'Health Care - Inpatient': msg({
    message: 'Health Care - Inpatient',
    context: 'Building kind label',
  }),
  'Health Care - Outpatient Clinic': msg({
    message: 'Health Care - Outpatient Clinic',
    context: 'Building kind label',
  }),
  'Health Care - Outpatient Uncategorized': msg({
    message: 'Health Care - Outpatient Uncategorized',
    context: 'Building kind label',
  }),
  'Health Care - Uncategorized': msg({
    message: 'Health Care - Uncategorized',
    context: 'Building kind label',
  }),
  Industrial: msg({ message: 'Industrial', context: 'Building kind label' }),
  Laboratory: msg({ message: 'Laboratory', context: 'Building kind label' }),
  'Lodging - Dormitory or fraternity/sorority': msg({
    message: 'Lodging - Dormitory or fraternity/sorority',
    context: 'Building kind label',
  }),
  'Lodging - Hotel': msg({
    message: 'Lodging - Hotel',
    context: 'Building kind label',
  }),
  'Lodging - Motel or inn': msg({
    message: 'Lodging - Motel or inn',
    context: 'Building kind label',
  }),
  'Manufactured Home': msg({
    message: 'Manufactured Home',
    context: 'Building kind label',
  }),
  'Manufacturing Facility': msg({
    message: 'Manufacturing Facility',
    context: 'Building kind label',
  }),
  'Mixed Use - Commercial and Residential': msg({
    message: 'Mixed Use - Commercial and Residential',
    context: 'Building kind label',
  }),
  'Mixed Use - Predominantly Commercial': msg({
    message: 'Mixed Use - Predominantly Commercial',
    context: 'Building kind label',
  }),
  'Mixed Use - Predominantly Residential': msg({
    message: 'Mixed Use - Predominantly Residential',
    context: 'Building kind label',
  }),
  'Multifamily - Condominiums': msg({
    message: 'Multifamily - Condominiums',
    context: 'Building kind label',
  }),
  'Multifamily - Uncategorized': msg({
    message: 'Multifamily - Uncategorized',
    context: 'Building kind label',
  }),
  'Nursing Home': msg({
    message: 'Nursing Home',
    context: 'Building kind label',
  }),
  'Office - Administrative or Professional': msg({
    message: 'Office - Administrative or Professional',
    context: 'Building kind label',
  }),
  'Office - Bank or other financial': msg({
    message: 'Office - Bank or other financial',
    context: 'Building kind label',
  }),
  'Office - Government': msg({
    message: 'Office - Government',
    context: 'Building kind label',
  }),
  'Office - Medical non diagnostic': msg({
    message: 'Office - Medical non diagnostic',
    context: 'Building kind label',
  }),
  'Office - Mixed use': msg({
    message: 'Office - Mixed use',
    context: 'Building kind label',
  }),
  'Office - Uncategorized': msg({
    message: 'Office - Uncategorized',
    context: 'Building kind label',
  }),
  'Parking Garage': msg({
    message: 'Parking Garage',
    context: 'Building kind label',
  }),
  'Public Assembly - Arena': msg({
    message: 'Public Assembly - Arena',
    context: 'Building kind label',
  }),
  'Public Assembly - Drama theater': msg({
    message: 'Public Assembly - Drama theater',
    context: 'Building kind label',
  }),
  'Public Assembly - Large Hall': msg({
    message: 'Public Assembly - Large Hall',
    context: 'Building kind label',
  }),
  'Public Assembly - Library': msg({
    message: 'Public Assembly - Library',
    context: 'Building kind label',
  }),
  'Public Assembly - Movie Theater': msg({
    message: 'Public Assembly - Movie Theater',
    context: 'Building kind label',
  }),
  'Public Assembly - Pool': msg({
    message: 'Public Assembly - Pool',
    context: 'Building kind label',
  }),
  'Public Assembly - Recreation': msg({
    message: 'Public Assembly - Recreation',
    context: 'Building kind label',
  }),
  'Public Assembly - Stadium': msg({
    message: 'Public Assembly - Stadium',
    context: 'Building kind label',
  }),
  'Public Assembly - Uncategorized': msg({
    message: 'Public Assembly - Uncategorized',
    context: 'Building kind label',
  }),
  'Public Safety - Courthouse': msg({
    message: 'Public Safety - Courthouse',
    context: 'Building kind label',
  }),
  'Public Safety - Fire or police station': msg({
    message: 'Public Safety - Fire or police station',
    context: 'Building kind label',
  }),
  'Public Safety - Penitentiary': msg({
    message: 'Public Safety - Penitentiary',
    context: 'Building kind label',
  }),
  'Public Safety - Uncategorized': msg({
    message: 'Public Safety - Uncategorized',
    context: 'Building kind label',
  }),
  'Religious worship': msg({
    message: 'Religious worship',
    context: 'Building kind label',
  }),
  'Residential - Unknown': msg({
    message: 'Residential - Unknown',
    context: 'Building kind label',
  }),
  'Retail - Big Box (> 50K sf)': msg({
    message: 'Retail - Big Box (> 50K sf)',
    context: 'Building kind label',
  }),
  'Retail - Enclosed mall': msg({
    message: 'Retail - Enclosed mall',
    context: 'Building kind label',
  }),
  'Retail - Small Box (< 50K sf)': msg({
    message: 'Retail - Small Box (< 50K sf)',
    context: 'Building kind label',
  }),
  'Retail - Strip shopping mall': msg({
    message: 'Retail - Strip shopping mall',
    context: 'Building kind label',
  }),
  'Retail - Uncategorized': msg({
    message: 'Retail - Uncategorized',
    context: 'Building kind label',
  }),
  'Retail - Vehicle dealership/showroom': msg({
    message: 'Retail - Vehicle dealership/showroom',
    context: 'Building kind label',
  }),
  'Service - Repair shop': msg({
    message: 'Service - Repair shop',
    context: 'Building kind label',
  }),
  'Service - Uncategorized': msg({
    message: 'Service - Uncategorized',
    context: 'Building kind label',
  }),
  'Service - Vehicle service/repair shop': msg({
    message: 'Service - Vehicle service/repair shop',
    context: 'Building kind label',
  }),
  'Single Family - Attached': msg({
    message: 'Single Family - Attached',
    context: 'Building kind label',
  }),
  'Single Family - Detached': msg({
    message: 'Single Family - Detached',
    context: 'Building kind label',
  }),
  'Single Family - Uncategorized': msg({
    message: 'Single Family - Uncategorized',
    context: 'Building kind label',
  }),
  'Transportation Terminal': msg({
    message: 'Transportation Terminal',
    context: 'Building kind label',
  }),
  'Warehouse - Distribution or Shipping center': msg({
    message: 'Warehouse - Distribution or Shipping center',
    context: 'Building kind label',
  }),
  'Warehouse - Non-refrigerated': msg({
    message: 'Warehouse - Non-refrigerated',
    context: 'Building kind label',
  }),
  'Warehouse - Refrigerated': msg({
    message: 'Warehouse - Refrigerated',
    context: 'Building kind label',
  }),
  'Warehouse - Self-storage': msg({
    message: 'Warehouse - Self-storage',
    context: 'Building kind label',
  }),
  'Warehouse - Uncategorized': msg({
    message: 'Warehouse - Uncategorized',
    context: 'Building kind label',
  }),
};

const OLD_BUILDING_KINDS = [
  'Office',
  'CommercialIndustrial',
  'DataCenterColocation',
  'DataCenterNonColocation',
  'MedicalFacilityNonDiagnostic',
  'Laboratory',
  'Residential',
  'Restaurant',
  'Retail',
  'Warehouse',
  'WarehouseRefrigerated',
  'Other',
] as const;
export type OldBuildingKind = (typeof OLD_BUILDING_KINDS)[number];

export const OLD_BUILDING_TYPE_MAPPING: Partial<
  Record<OldBuildingKind, BuildingKind>
> = {
  Office: 'Office - Administrative or Professional',
  Residential: 'Single Family - Uncategorized',
  Restaurant: 'Food Service - Restaurant or cafeteria',
  Retail: 'Retail - Uncategorized',
  WarehouseRefrigerated: 'Warehouse - Refrigerated',
  Warehouse: 'Warehouse - Non-refrigerated',
  DataCenterColocation: 'Data Center - Colocated',
  DataCenterNonColocation: 'Data Center - Non-Colocated',
  MedicalFacilityNonDiagnostic: 'Office - Medical non diagnostic',
  CommercialIndustrial: 'Industrial',
  Other: 'Commercial - Uncategorized',
};

export const NEW_OR_OLD_BUILDING_KINDS = [
  ...BUILDING_KINDS_H1_2023,
  ...OLD_BUILDING_KINDS,
] as const;

export type NewOrOldBuildingKind = (typeof NEW_OR_OLD_BUILDING_KINDS)[number];

export type BuildingKind = (typeof BUILDING_KINDS_H1_2023)[number];

export type PartialBuilding = PartialNull<
  Pick<
    BuildingFieldsType,
    | 'streetAddress'
    | 'streetAddress2'
    | 'city'
    | 'state'
    | 'country'
    | 'postalCode'
    | 'buildingKind'
    | 'buildingSubkind'
    | 'startYearMonth'
    | 'endYearMonth'
    | 'leaseType'
    | 'sizeUnit'
  > & {
    periods: Array<Omit<BuildingPeriodType, 'id'>>;
  }
>;

type MinimalBuildingPeriod = Pick<
  BuildingPeriodType,
  'kind' | 'sizeQuantity' | 'partialCleanPowerPercent'
>;

interface BuildingPeriodBuildingSizeHelper extends MinimalBuildingPeriod {
  kind: 'BuildingSize';
  sizeQuantity: number;
}

interface BuildingPeriodCleanPowerHelper extends MinimalBuildingPeriod {
  kind: 'CleanPower';
  cleanPower: BuildingCleanPowerStatus;
  partialCleanPowerPercent: number;
}

type BuildingPeriodBuildingSize<T extends MinimalBuildingPeriod> = T &
  BuildingPeriodBuildingSizeHelper;

type BuildingPeriodCleanPower<T extends MinimalBuildingPeriod> = T &
  BuildingPeriodCleanPowerHelper;

// Used for the lease type dropdown and should contain ALL possible enum values.
// We used to have an option called "Unknown" ("I don't know"), which we no
// longer support. Legacy buildings might still have it, but they cannot be
// completed until changed.
export const ORDERED_LEASE_TYPES = [
  BuildingLeaseType.CoworkingOrServicedOffice,
  BuildingLeaseType.Owned,
  BuildingLeaseType.Leased,
  BuildingLeaseType.DownstreamLeasePayingUtilities,
  BuildingLeaseType.DownstreamLeaseNotPayingUtilities,
  BuildingLeaseType.DownstreamSubleasePayingUtilities,
  BuildingLeaseType.DownstreamSubleaseNotPayingUtilities,
  BuildingLeaseType.UpstreamSublease,
  BuildingLeaseType.Franchise,
  BuildingLeaseType.SoldBuilding,
] as const;

export function partitionBuildingPeriods<T extends MinimalBuildingPeriod>(
  periods: Array<T> | undefined | null
): {
  sizePeriods: Array<BuildingPeriodBuildingSize<T>>;
  cleanPowerPeriods: Array<BuildingPeriodCleanPower<T>>;
} {
  const sizePeriods = (periods ?? []).filter(
    (p: MinimalBuildingPeriod): p is BuildingPeriodBuildingSize<T> =>
      p.kind === BuildingPeriodKind.BuildingSize
  );
  const cleanPowerPeriods = (periods ?? []).filter(
    (p: MinimalBuildingPeriod): p is BuildingPeriodCleanPower<T> =>
      p.kind === BuildingPeriodKind.CleanPower
  );
  return { sizePeriods, cleanPowerPeriods };
}

// Filter out BuildingPeriods associated with custom fields that have been
// deleted, or are no longer associated with buildings or utilities
export function filterBuildingPeriodsByCustomFieldMap<
  T extends { kind: BuildingPeriodKind; customFieldId: string | null },
>(periods: Array<T>, customFieldMap: Record<string, {}>): Array<T> {
  return periods.filter(
    (period) =>
      period.kind !== 'CustomField' ||
      customFieldMap[
        must(
          period.customFieldId,
          'Expected CustomField period to have a customFieldId'
        )
      ] !== undefined
  );
}

export function parseIntValue(value: string): number;
export function parseIntValue(value: string | null): number | null;
export function parseIntValue(value: string | null): number | null {
  if (value === null) return null;
  const valueInt = parseInt(value, 10);
  return isNaN(valueInt) ? 0 : valueInt;
}

export function parseFloatValue(value: string | null): number | null {
  if (value === null) return null;
  const valueFloat = parseFloat(value);
  return isNaN(valueFloat) ? 0 : valueFloat;
}

function getBuildingKindDisplayValueArr(): Array<{
  buildingKind: OldBuildingKind;
  displayName: string;
}> {
  return [
    {
      buildingKind: 'Office',
      displayName: t({ message: 'Office', context: 'Building kind' }),
    },
    {
      buildingKind: 'Restaurant',
      displayName: t({ message: 'Restaurant', context: 'Building kind' }),
    },
    {
      buildingKind: 'Retail',
      displayName: t({ message: 'Retail', context: 'Building kind' }),
    },
    {
      buildingKind: 'Laboratory',
      displayName: t({ message: 'Laboratory', context: 'Building kind' }),
    },
    {
      buildingKind: 'Other',
      displayName: t({ message: 'Other', context: 'Building kind' }),
    },
    {
      buildingKind: 'Residential',
      displayName: t({ message: 'Residential', context: 'Building kind' }),
    },
    {
      buildingKind: 'Warehouse',
      displayName: t({
        message: 'Warehouse (general)',
        context: 'Building kind',
      }),
    },
    {
      buildingKind: 'WarehouseRefrigerated',
      displayName: t({
        message: 'Warehouse (refrigerated)',
        context: 'Building kind',
      }),
    },
    {
      buildingKind: 'DataCenterColocation',
      displayName: t({
        message: 'Data Center - Colocated',
        context: 'Building kind',
      }),
    },
    {
      buildingKind: 'DataCenterNonColocation',
      displayName: t({
        message: 'Data Center - Non-Colocated',
        context: 'Building kind',
      }),
    },
    {
      buildingKind: 'CommercialIndustrial',
      displayName: t({
        message: 'Commercial building used for industrial purposes',
        context: 'Building kind',
      }),
    },
    {
      buildingKind: 'MedicalFacilityNonDiagnostic',
      displayName: t({
        message: 'Medical facility (non-diagnostic)',
        context: 'Building kind',
      }),
    },
  ];
}

export function getBuildingKindDescriptions(): Record<
  (typeof BUILDING_KINDS_H1_2023)[number],
  string
> {
  return {
    '5+ Unit Building': t({
      message: `A multifamily residential building with five or more dwelling units. It typically consists of apartments or condominiums designed to accommodate multiple families or individuals.`,
      comment: 'Description of a "5+ Unit Building" building kind',
    }),
    'Apartment Unit': t({
      message: `A self-contained living space within a larger residential building. It is typically part of a multifamily complex and features separate rooms for living, sleeping, cooking, and bathing.`,
      comment: 'Description of a "Apartment Unit" building kind',
    }),
    'Commercial - Uncategorized': t({
      message: `A commercial building that does not fit into any specific category or classification. It may refer to a variety of non-residential structures used for business purposes, such as offices, retail spaces, or service establishments.`,
      comment: 'Description of a "Commercial - Uncategorized" building kind',
    }),
    'Convenience store': t({
      message: `A small retail establishment that offers a limited selection of everyday items, such as groceries, snacks, beverages, and personal care products. Convenience stores are typically open for extended hours and provide quick and convenient shopping options.`,
      comment: 'Description of a "Convenience store" building kind',
    }),
    'Convenience store with gas station': t({
      message: `A combination of a convenience store and a gas station. It offers fueling services for vehicles along with the convenience store's usual range of products.`,
      comment:
        'Description of a "Convenience store with gas station" building kind',
    }),
    'Data Center - Colocated': t({
      message: `A facility that houses multiple computer servers and related equipment. Colocated data centers are shared spaces where multiple organizations rent or lease server space and network infrastructure.`,
      comment: 'Description of a "Data Center - Colocated" building kind',
    }),
    'Data Center - Non-Colocated': t({
      message: `A data center facility that is dedicated to a single organization or company. Non-colocated data centers are usually owned and operated by the organization itself.`,
      comment: 'Description of a "Data Center - Non-Colocated" building kind',
    }),
    'Education - College or university': t({
      message: `A building or campus dedicated to higher education, offering undergraduate and postgraduate programs. It includes lecture halls, classrooms, laboratories, libraries, administrative offices, and other facilities necessary for teaching and research.`,
      comment:
        'Description of a "Education - College or university" building kind',
    }),
    'Education - Elementary or middle school': t({
      message: `A building or campus that provides education for young students, typically ranging from kindergarten to eighth grade. It includes classrooms, playgrounds, administrative offices, and other facilities suitable for younger learners.`,
      comment:
        'Description of a "Education - Elementary or middle school" building kind',
    }),
    'Education - High school': t({
      message: `A building or campus that offers education for students in grades nine through twelve or the equivalent. It provides classrooms, laboratories, sports facilities, administrative offices, and other amenities required for secondary education.`,
      comment: 'Description of a "Education - High school" building kind',
    }),
    'Education - Other classroom': t({
      message: `A dedicated space or building designed for educational purposes. It may include specialized classrooms for specific subjects or activities, such as art studios, music rooms, computer labs, or vocational training facilities.`,
      comment: 'Description of a "Education - Other classroom" building kind',
    }),
    'Education - Preschool or daycare': t({
      message: `A facility designed to provide early childhood education and care for young children, typically between the ages of two and five. It offers a safe and nurturing environment where children can engage in age-appropriate activities and learning.`,
      comment:
        'Description of a "Education - Preschool or daycare" building kind',
    }),
    'Education - Uncategorized': t({
      message: `An educational building or facility that does not fit into any specific category or classification. It may refer to unique or specialized educational institutions or buildings.`,
      comment: 'Description of a "Education - Uncategorized" building kind',
    }),
    'Food Sales': t({
      message: `A commercial building or space dedicated to the sale of food products. It includes grocery stores, supermarkets, and other retail establishments primarily focused on selling food items.`,
      comment: 'Description of a "Food Sales" building kind',
    }),
    'Food Service - Fast food': t({
      message: `A building or establishment that specializes in providing quick-service food options. Fast food restaurants typically offer a limited menu, self-service ordering, and speedy meal preparation.`,
      comment: 'Description of a "Food Service - Fast food" building kind',
    }),
    'Food Service - Restaurant or cafeteria': t({
      message: `A building or establishment that serves prepared meals and beverages to customers. It may include full-service restaurants, casual dining establishments, cafeterias, or buffet-style eateries.`,
      comment:
        'Description of a "Food Service - Restaurant or cafeteria" building kind',
    }),
    'Food Service - Uncategorized': t({
      message: `A food service establishment that does not fit into any specific category or classification. It may refer to unique or specialized food-related businesses or facilities.`,
      comment: 'Description of a "Food Service - Uncategorized" building kind',
    }),
    'Grocery store or food market': t({
      message: `A retail store that primarily sells a wide variety of food products, including fresh produce, packaged goods, dairy products, and household essentials.`,
      comment: 'Description of a "Grocery store or food market" building kind',
    }),
    'Health Care - Inpatient': t({
      message: `A medical facility designed to provide care for patients requiring overnight stays or extended treatment. It may include hospitals, medical centers, or specialized inpatient clinics.`,
      comment: 'Description of a "Health Care - Inpatient" building kind',
    }),
    'Health Care - Outpatient Clinic': t({
      message: `A medical facility that offers healthcare services to patients who do not require overnight stays. Outpatient clinics provide diagnosis, treatment, and preventive care on an outpatient basis.`,
      comment:
        'Description of a "Health Care - Outpatient Clinic" building kind',
    }),
    'Health Care - Outpatient Uncategorized': t({
      message: `An outpatient healthcare facility that does not fit into any specific category or classification. It may refer to specialized clinics or healthcare centers with unique services or functions.`,
      comment:
        'Description of a "Health Care - Outpatient Uncategorized" building kind',
    }),
    'Health Care - Uncategorized': t({
      message: `A healthcare building or facility that does not fit into any specific category or classification. It may refer to unique or specialized healthcare institutions or buildings.`,
      comment: 'Description of a "Health Care - Uncategorized" building kind',
    }),
    Industrial: t({
      message: `A building or complex used for industrial activities such as manufacturing, production, or warehousing. Industrial buildings are typically designed to accommodate heavy machinery, storage, and logistics operations.`,
      comment: 'Description of an "Industrial" building kind',
    }),
    Laboratory: t({
      message: `A specialized facility equipped with scientific instruments and equipment for research, experimentation, and analysis. Laboratories are commonly found in academic institutions, healthcare organizations, and industrial research facilities.`,
      comment: 'Description of a "Laboratory" building kind',
    }),
    'Lodging - Dormitory or fraternity/sorority': t({
      message: `A residential building or facility that provides accommodation for students, typically associated with colleges or universities. Dormitories or fraternity/sorority houses offer shared living spaces and communal facilities.`,
      comment:
        'Description of a "Lodging - Dormitory or fraternity/sorority" building kind',
    }),
    'Lodging - Hotel': t({
      message: `A commercial building or establishment that provides temporary accommodations, typically for travelers or tourists. Hotels offer guest rooms or suites along with amenities and services such as restaurants, meeting rooms, and recreational facilities.`,
      comment: 'Description of a "Lodging - Hotel" building kind',
    }),
    'Lodging - Motel or inn': t({
      message: `A lodging establishment that offers accommodations primarily to motorists or travelers. Motels or inns often feature rooms with direct access to a parking area, allowing easy access and convenience for guests.`,
      comment: 'Description of a "Lodging - Motel or inn" building kind',
    }),
    'Manufactured Home': t({
      message: `A prefabricated dwelling constructed off-site and transported to a specific location for installation. Manufactured homes are built in factories and then assembled on-site, offering a more affordable housing option.`,
      comment: 'Description of a "Manufactured Home" building kind',
    }),
    'Manufacturing Facility': t({
      message: `A building or complex where goods are produced or manufactured. Manufacturing facilities house machinery, assembly lines, storage areas, and other infrastructure necessary for industrial production. `,
      comment: 'Description of a "Manufacturing Facility" building kind',
    }),
    'Mixed Use - Commercial and Residential': t({
      message: `A building or development that combines both commercial and residential uses. It may feature ground-level retail or office spaces with residential units on the upper floors.`,
      comment:
        'Description of a "Mixed Use - Commercial and Residential" building kind',
    }),
    'Mixed Use - Predominantly Commercial': t({
      message: `A building or development that primarily focuses on commercial uses. It may include retail spaces, offices, and other commercial establishments, with limited or no residential components.`,
      comment:
        'Description of a "Mixed Use - Predominantly Commercial" building kind',
    }),
    'Mixed Use - Predominantly Residential': t({
      message: `A building or development that primarily focuses on residential uses. It may include apartments, condominiums, or townhouses, with limited or no commercial components.`,
      comment:
        'Description of a "Mixed Use - Predominantly Residential" building kind',
    }),
    'Multifamily - Condominiums': t({
      message: `A residential building or complex that consists of individually owned units within a larger structure. Each unit is privately owned, while common areas and facilities are shared among the residents.`,
      comment: 'Description of a "Multifamily - Condominiums" building kind',
    }),
    'Multifamily - Uncategorized': t({
      message: `A multifamily residential building or complex that does not fit into any specific category or classification. It may refer to unique or specialized multifamily housing options.`,
      comment: 'Description of a "Multifamily - Uncategorized" building kind',
    }),
    'Nursing Home': t({
      message: `A residential facility that provides long-term care, support, and medical services for elderly or disabled individuals who require assistance with daily activities. Nursing homes offer accommodation, meals, and medical supervision.`,
      comment: 'Description of a "Nursing Home" building kind',
    }),
    'Office - Administrative or Professional': t({
      message: `A building or space designed for administrative or professional activities. It may house corporate offices, government agencies, professional service providers, or administrative departments.`,
      comment:
        'Description of an "Office - Administrative or Professional" building kind',
    }),
    'Office - Bank or other financial': t({
      message: `A building or space dedicated to financial services, such as banks, credit unions, or other financial institutions. These offices typically offer banking, investment, or insurance services.`,
      comment:
        'Description of an "Office - Bank or other financial" building kind',
    }),
    'Office - Government': t({
      message: `A building or space used by government agencies or departments for administrative, legislative, or public service functions. Government offices serve as workplaces for government employees and provide public access to government services.`,
      comment: 'Description of an "Office - Government" building kind',
    }),
    'Office - Medical non diagnostic': t({
      message: `A building or space used for medical or healthcare-related administrative purposes, excluding diagnostic procedures. It may include medical offices, clinics, or administrative areas within hospitals.`,
      comment:
        'Description of an "Office - Medical non diagnostic" building kind',
    }),
    'Office - Mixed use': t({
      message: `A building or space that combines office use with other complementary uses, such as retail or commercial establishments. It creates a mixed-use environment, offering a variety of services in a single location.`,
      comment: 'Description of an "Office - Mixed use" building kind',
    }),
    'Office - Uncategorized': t({
      message: `An office building or space that does not fit into any specific category or classification. It may refer to unique or specialized office environments.`,
      comment: 'Description of an "Office - Uncategorized" building kind',
    }),
    'Parking Garage': t({
      message: `A structure designed to provide parking spaces for vehicles. Parking garages typically consist of multiple levels or floors dedicated to parking and may include automated systems for vehicle storage and retrieval.`,
      comment: 'Description of a "Parking Garage" building kind',
    }),
    'Public Assembly - Arena': t({
      message: `A large facility designed for hosting sports events, concerts, exhibitions, and other public gatherings. Arenas provide seating for large audiences and often include amenities such as concessions, box offices, and backstage areas.`,
      comment: 'Description of a "Public Assembly - Arena" building kind',
    }),
    'Public Assembly - Drama theater': t({
      message: `A building or venue designed for theatrical performances, including plays, musicals, and other live productions. Drama theaters feature stages, seating areas, backstage facilities, and technical equipment for performances.`,
      comment:
        'Description of a "Public Assembly - Drama theater" building kind',
    }),
    'Public Assembly - Large Hall': t({
      message: `A spacious hall or auditorium designed to accommodate large gatherings, events, or conferences. Large halls may be used for concerts, conferences, exhibitions, or other public assemblies.`,
      comment: 'Description of a "Public Assembly - Large Hall" building kind',
    }),
    'Public Assembly - Library': t({
      message: `A building or facility that houses collections of books, periodicals, and other materials for public use. Libraries often provide reading areas, study spaces, and other amenities to support learning and research.`,
      comment: 'Description of a "Public Assembly - Library" building kind',
    }),
    'Public Assembly - Movie Theater': t({
      message: `A building or venue equipped with screens or theaters for public movie screenings. Movie theaters offer seating, projection systems, sound systems, and other amenities for a cinematic experience.`,
      comment:
        'Description of a "Public Assembly - Movie Theater" building kind',
    }),
    'Public Assembly - Pool': t({
      message: `A facility that provides swimming and recreational activities for the public. Public pools may include indoor or outdoor swimming areas, changing rooms, showers, and additional amenities.`,
      comment: 'Description of a "Public Assembly - Pool" building kind',
    }),
    'Public Assembly - Recreation': t({
      message: `A building or facility designed for recreational activities and leisure pursuits. It may include sports complexes, fitness centers, community centers, or other recreational venues.`,
      comment: 'Description of a "Public Assembly - Recreation" building kind',
    }),
    'Public Assembly - Stadium': t({
      message: `A large sports venue designed to host sporting events, including football, baseball, soccer, or other outdoor sports. Stadiums provide seating for spectators, playing fields, concession stands, and other amenities.`,
      comment: 'Description of a "Public Assembly - Stadium" building kind',
    }),
    'Public Assembly - Uncategorized': t({
      message: `A public assembly building or facility that does not fit into any specific category or classification. It may refer to unique or specialized public assembly venues.`,
      comment:
        'Description of a "Public Assembly - Uncategorized" building kind',
    }),
    'Public Safety - Courthouse': t({
      message: `A building or complex that serves as a judicial facility where legal proceedings, trials, and other court-related activities take place. Courthouses typically include courtrooms, offices for judges and court personnel, and related facilities.`,
      comment: 'Description of a "Public Safety - Courthouse" building kind',
    }),
    'Public Safety - Fire or police station': t({
      message: `A building or facility used by fire departments or police departments for emergency response, administrative operations, and public safety services. Fire or police stations often house equipment, vehicles, and personnel necessary for emergency response.`,
      comment:
        'Description of a "Public Safety - Fire or police station" building kind',
    }),
    'Public Safety - Penitentiary': t({
      message: `A secure facility designed for the incarceration and rehabilitation of convicted criminals. Penitentiaries or prisons provide housing, security measures, and programs aimed at inmate rehabilitation.`,
      comment: 'Description of a "Public Safety - Penitentiary" building kind',
    }),
    'Public Safety - Uncategorized': t({
      message: `A public safety building or facility that does not fit into any specific category or classification. It may refer to unique or specialized public safety institutions or buildings.`,
      comment: 'Description of a "Public Safety - Uncategorized" building kind',
    }),
    'Religious worship': t({
      message: `A building or structure dedicated to religious worship, ceremonies, and gatherings. It may include churches, mosques, synagogues, temples, or other places of worship.`,
      comment: 'Description of a "Religious worship" building kind',
    }),
    'Residential - Unknown': t({
      message: `A building or dwelling of unknown residential type or classification. It may refer to an unidentified or unspecified residential structure.`,
      comment: 'Description of a "Residential - Unknown" building kind',
    }),
    'Retail - Big Box (> 50K sf)': t({
      message: `A large retail store or establishment that exceeds 50,000 square feet in size. Big box retailers often offer a wide range of products across multiple categories, such as electronics, home goods, or sporting equipment.`,
      comment: 'Description of a "Retail - Big Box (> 50K sf)" building kind',
    }),
    'Retail - Enclosed mall': t({
      message: `A large indoor shopping center that features multiple retail stores and establishments. Enclosed malls provide a climate-controlled environment with common areas, food courts, and amenities for shoppers.`,
      comment: 'Description of a "etail - Enclosed mall" building kind',
    }),
    'Retail - Small Box (< 50K sf)': t({
      message: `A smaller retail store or establishment that occupies less than 50,000 square feet of space. Small box retailers may specialize in specific products or offer a limited range of goods.`,
      comment: 'Description of a "Retail - Small Box (< 50K sf)" building kind',
    }),
    'Retail - Strip shopping mall': t({
      message: `A commercial development that consists of a row of connected retail stores or establishments. Strip shopping malls are typically located along major roads or highways and offer convenience and easy access to retail services.`,
      comment: 'Description of a "Retail - Strip shopping mall" building kind',
    }),
    'Retail - Uncategorized': t({
      message: `A retail building or establishment that does not fit into any specific category or classification. It may refer to unique or specialized retail businesses or facilities.`,
      comment: 'Description of a "Retail - Uncategorized" building kind',
    }),
    'Retail - Vehicle dealership/showroom': t({
      message: `A retail establishment that specializes in the sale and display of vehicles, such as cars, motorcycles, or recreational vehicles. Vehicle dealerships or showrooms offer a range of models, financing options, and customer services.`,
      comment:
        'Description of a "Retail - Vehicle dealership/showroom" building kind',
    }),
    'Service - Repair shop': t({
      message: `A building or establishment that provides repair or maintenance services for various products or equipment. Repair shops may specialize in specific items, such as automotive repair, electronics repair, or appliance repair.`,
      comment: 'Description of a "Service - Repair shop" building kind',
    }),
    'Service - Uncategorized': t({
      message: `A service-oriented building or establishment that does not fit into any specific category or classification. It may refer to unique or specialized service businesses or facilities.`,
      comment: 'Description of a "Service - Uncategorized" building kind',
    }),
    'Service - Vehicle service/repair shop': t({
      message: `A specialized service establishment that offers repair, maintenance, or servicing for vehicles. Vehicle service/repair shops may focus on specific types of vehicles or provide general automotive services.`,
      comment:
        'Description of a "Service - Vehicle service/repair shop" building kind',
    }),
    'Single Family - Attached': t({
      message: `A residential building or dwelling that shares a common wall or structure with adjacent units. Attached single-family homes, such as townhouses or row houses, typically have multiple floors and individual entrances.`,
      comment: 'Description of a "Single Family - Attached" building kind',
    }),
    'Single Family - Detached': t({
      message: `A freestanding residential building or dwelling that stands alone and is not attached to any other structure. Detached single-family homes offer independent living spaces with private yards or gardens.`,
      comment: 'Description of a "Single Family - Detached" building kind',
    }),
    'Single Family - Uncategorized': t({
      message: `A single-family residential building or dwelling that does not fit into any specific category or classification. It may refer to unique or specialized single-family housing options.`,
      comment: 'Description of a "Single Family - Uncategorized" building kind',
    }),
    'Transportation Terminal': t({
      message: `A building or facility that serves as a hub for various modes of transportation, such as airports, bus stations, train stations, or ferry terminals. Transportation terminals provide passenger services, ticketing, waiting areas, and other amenities.`,
      comment: 'Description of a "Transportation Terminal" building kind',
    }),
    'Warehouse - Distribution or Shipping center': t({
      message: `A large building or facility used for the storage, sorting, and distribution of goods or products. Distribution or shipping centers handle the movement and logistics of goods within a supply chain.`,
      comment:
        'Description of a "Warehouse - Distribution or Shipping center" building kind',
    }),
    'Warehouse - Non-refrigerated': t({
      message: `A warehouse or storage facility that does not have temperature-controlled or refrigeration capabilities. Non-refrigerated warehouses are used for storing goods that do not require specific temperature conditions.`,
      comment: 'Description of a "Warehouse - Non-refrigerated" building kind',
    }),
    'Warehouse - Refrigerated': t({
      message: `A warehouse or storage facility equipped with refrigeration systems for the storage of perishable goods or temperature-sensitive products. Refrigerated warehouses maintain specific temperature ranges to preserve the quality of stored items.`,
      comment: 'Description of a "Warehouse - Refrigerated" building kind',
    }),
    'Warehouse - Self-storage': t({
      message: `A facility that provides individual storage units or lockers for personal or business use. Self-storage warehouses allow individuals or organizations to rent storage space on a short-term or long-term basis.`,
      comment: 'Description of a "Warehouse - Self-storage" building kind',
    }),
    'Warehouse - Uncategorized': t({
      message: `A warehouse or storage facility that does not fit into any specific category or classification. It may refer to unique or specialized warehouse structures or operations.`,
      comment: 'Description of a "Warehouse - Uncategorized" building kind',
    }),
  };
}

export function getBuildingKindDisplayValue(
  buildingKind: string,
  i18nInstance: I18n = i18n
): string {
  if (isIncludedIn(buildingKind, BUILDING_KINDS_H1_2023)) {
    return i18nInstance._(BUILDING_KIND_H1_2023_DISPLAY_LABELS[buildingKind]);
  }
  const match = getBuildingKindDisplayValueArr().find(
    (bkd) => bkd.buildingKind === buildingKind
  );
  if (match === undefined) {
    return t`Unknown building kind - ${buildingKind}`;
  }
  return match.displayName;
}

export function getLeaseTypeDisplayValue(leaseType: BuildingLeaseType): string {
  switch (leaseType) {
    case BuildingLeaseType.Owned:
      return t({ message: `Owned by us`, context: 'Building lease type' });
    case BuildingLeaseType.Leased:
      return t({ message: `Leased to us`, context: 'Building lease type' });
    case BuildingLeaseType.UpstreamSublease:
      return t({ message: `Subleased to us`, context: 'Building lease type' });
    case BuildingLeaseType.DownstreamLeasePayingUtilities:
      return t({
        message: `Owned by us, leased to someone else, and we pay utilities`,
        context: 'Building lease type',
      });
    case BuildingLeaseType.DownstreamLeaseNotPayingUtilities:
      return t({
        message: `Owned by us, leased to someone else, and we do not pay utilities`,
        context: 'Building lease type',
      });
    case BuildingLeaseType.DownstreamSubleaseNotPayingUtilities:
      return t({
        message: `Leased to us and subleased to someone else, and we do not pay utilities`,
        context: 'Building lease type',
      });
    case BuildingLeaseType.DownstreamSubleasePayingUtilities:
      return t({
        message: `Leased to us and subleased to someone else, and we pay utilities`,
        context: 'Building lease type',
      });
    case BuildingLeaseType.CoworkingOrServicedOffice:
      return t({
        message: `Coworking space or serviced office`,
        context: 'Building lease type',
      });
    case BuildingLeaseType.SoldBuilding:
      return t({ message: `Sold building`, context: 'Building lease type' });
    case BuildingLeaseType.Franchise:
      return t({ message: `Franchise`, context: 'Building lease type' });
    case BuildingLeaseType.Unknown:
      return t({
        message: "I don't know",
        context: "I don't know the building lease type",
      });
    default:
      assertNever(leaseType);
  }
}

export function getIsBuildingsDatasource(datasource?: {
  dataset: {
    canonicalDataset: null | { kind: CanonicalDatasetKind | null };
  } | null;
}): boolean {
  if (!datasource?.dataset?.canonicalDataset?.kind) {
    return false;
  }

  return (
    CanonicalDatasetKind.Buildings === datasource.dataset.canonicalDataset.kind
  );
}

export function getIsUtilitiesDatasource(datasource?: {
  dataset: {
    canonicalDataset: null | { kind: CanonicalDatasetKind | null };
  } | null;
}): boolean {
  if (!datasource?.dataset?.canonicalDataset?.kind) {
    return false;
  }

  return (
    CanonicalDatasetKind.Utilities === datasource.dataset.canonicalDataset.kind
  );
}

export function getKindIsFacilitiesDataset(
  kind?: CanonicalDatasetKind | null
): boolean {
  return (
    kind === CanonicalDatasetKind.Buildings ||
    kind === CanonicalDatasetKind.Utilities
  );
}

export function joinAddressParts(
  parts: Array<string | null | undefined>
): string {
  // TODO: i18n (please resolve or remove this TODO line if legit)
  // eslint-disable-next-line @watershed/no-join-commas
  return parts.map(nullifyEmptyString).filter(isNotNullish).join(', ');
}

export function facilityNameParts(
  {
    buildingName,
    streetAddress,
    city,
    country,
  }: Pick<
    PartialBuilding,
    'streetAddress' | 'streetAddress2' | 'city' | 'state' | 'country'
  > & {
    buildingName: string;
  },
  {
    withSeparator = true,
  }: {
    /**
     * Adds a separator to the end of the building name if there's an address, city, or country.
     */
    withSeparator?: boolean;
  } = {}
): {
  primary: string;
  secondary: string;
} {
  const parts = [streetAddress, city, country].filter((x) => !!x);
  return {
    primary:
      buildingName + (withSeparator ? (parts.length > 0 ? ',' : '') : ''),
    secondary: joinAddressParts(parts),
  };
}

// TODO: DI-10473 intl-ize this
export function formatBuildingSize(
  sizeQuantity: number,
  sizeUnit: BuildingSizeUnit | null | undefined,
  i18nInstance: I18n = i18n
): string {
  // TODO: i18n (please resolve or remove this TODO line if legit)
  // eslint-disable-next-line @watershed/require-locale-argument
  return `${formatNumber(sizeQuantity, { maximumFractionDigits: 0 })} ${
    sizeUnit ? i18nInstance._(UNIT_NAMES.short[sizeUnit]) : ''
  }`;
}

/**
 * Extracts the latest building size from the provided periods and returns it
 * Requires the i18n instance to format the size based on locale, since it cannot be
 * imported in shared-universal
 */
export function processBuildingSizeFromPeriods(
  periods: Array<{
    sizeQuantity: number | null;
    kind: BuildingPeriodKind;
  }>,
  i18nInstance: I18n,
  sizeUnit?: BuildingSizeUnit | null
): { size: number; output: string } | undefined {
  const buildingSizePeriods = periods.filter(
    (period) => period.kind === BuildingPeriodKind.BuildingSize
  );
  const latestSizePeriod = buildingSizePeriods.at(-1);
  if (latestSizePeriod && latestSizePeriod.sizeQuantity) {
    const sizeOutput =
      buildingSizePeriods.length > 1
        ? // TODO: DI-10473 intl-ize this
          `${formatBuildingSize(
            latestSizePeriod.sizeQuantity,
            sizeUnit,
            i18nInstance
          )} (latest)`
        : formatBuildingSize(
            latestSizePeriod.sizeQuantity,
            sizeUnit,
            i18nInstance
          );
    return {
      size: latestSizePeriod.sizeQuantity,
      output: sizeOutput,
    };
  }
  return {
    size: 0,
    output: `${formatBuildingSize(0, sizeUnit, i18nInstance)}`,
  };
}

export function getLatestCustomColumnValueFromPeriods(
  customColumnId: string,
  periods: Array<{
    customFieldId: string | null;
    customFieldValue: string | null;
    kind: BuildingPeriodKind;
  }>
): string {
  const buildingCustomColumnPeriods = periods.filter(
    (period) => period.kind === BuildingPeriodKind.CustomField
  );
  const latestCustomColumn = buildingCustomColumnPeriods.findLast(
    (period) => period.customFieldId === customColumnId
  );
  return latestCustomColumn?.customFieldValue ?? '';
}

export function getLatestOrgUnitFromPeriods(
  orgUnitType: GQOrgUnitTypeWithOrgUnitsFragment,
  periods: Array<{
    orgUnitTypeStableId: string | null;
    orgUnitStableId: string | null;
    kind: BuildingPeriodKind;
  }>
): string | null {
  const buildingOrgUnitPeriods = periods.filter(
    (period) => period.kind === BuildingPeriodKind.OrgUnit
  );
  const latestOrgUnit = buildingOrgUnitPeriods.findLast(
    (period) => period.orgUnitTypeStableId === orgUnitType.stableId
  );
  const orgUnit = orgUnitType.orgUnits.find(
    (unit) => unit.stableId === latestOrgUnit?.orgUnitStableId
  );
  const value = orgUnit?.name ?? null;
  return value;
}

export const BEGIN_GENERATED_BUILDING_MAPPING_STRING =
  '-- BEGIN GENERATED MAPPING';
export const END_GENERATED_BUILDING_MAPPING_STRING = '-- END GENERATED MAPPING';

export const BUILDING_ACTIVITY_TYPE = 'building_occupation' as const;
export const STATIONARY_COMBUSTION_ACTIVITY_TYPE =
  'building_stationary_combustion' as const;
export const ELECTRICITY_CONSUMPTION_ACTIVITY_TYPE =
  'electricity_consumption' as const;
export const ELECTRICITY_CONSUMPTION_DATACENTER_ACTIVITY_TYPE =
  'electricity_consumption_datacenter' as const;
export const WASTE_ACTIVITY_TYPE = 'building_waste' as const;
export const WATER_CONSUMPTION_ACTIVITY_TYPE = 'water_consumption' as const;
export const WATER_USAGE_ACTIVITY_TYPE = 'water_usage' as const;
export const WATER_RECYCLING_ACTIVITY_TYPE = 'water_recycling' as const;
export const WATER_STORED_ACTIVITY_TYPE = 'water_storage' as const;
export const WATER_DISCHARGE_ACTIVITY_TYPE = 'water_discharge' as const;
export const REFRIGERANTS_ACTIVITY_TYPE = 'refrigerants' as const;

export const WATER_CSRD_ACTIVITY_TYPES = [
  WATER_CONSUMPTION_ACTIVITY_TYPE,
  WATER_RECYCLING_ACTIVITY_TYPE,
  WATER_STORED_ACTIVITY_TYPE,
  WATER_DISCHARGE_ACTIVITY_TYPE,
];

export const WATER_ACTIVITY_TYPES = [
  WATER_USAGE_ACTIVITY_TYPE,
  ...WATER_CSRD_ACTIVITY_TYPES,
] as const;

export const WATER_ACTIVITY_TYPE_TO_WATER_USE_KIND: Record<
  (typeof WATER_ACTIVITY_TYPES)[number],
  GQBuildingWaterUseKind
> = {
  [WATER_CONSUMPTION_ACTIVITY_TYPE]: GQBuildingWaterUseKind.Consumed,
  [WATER_USAGE_ACTIVITY_TYPE]: GQBuildingWaterUseKind.Used,
  [WATER_RECYCLING_ACTIVITY_TYPE]: GQBuildingWaterUseKind.Recycled,
  [WATER_STORED_ACTIVITY_TYPE]: GQBuildingWaterUseKind.Stored,
  [WATER_DISCHARGE_ACTIVITY_TYPE]: GQBuildingWaterUseKind.Discharged,
};

export const UTILITIES_ACTIVITY_TYPES = [
  STATIONARY_COMBUSTION_ACTIVITY_TYPE,
  ELECTRICITY_CONSUMPTION_ACTIVITY_TYPE,
  ELECTRICITY_CONSUMPTION_DATACENTER_ACTIVITY_TYPE,
  WASTE_ACTIVITY_TYPE,
  ...WATER_ACTIVITY_TYPES,
  REFRIGERANTS_ACTIVITY_TYPE,
] as const;

export type UtilitiesActivityType = (typeof UTILITIES_ACTIVITY_TYPES)[number];

export const UTILITIES_ACTIVITY_TYPES_FOR_BART_GENERATION = [
  STATIONARY_COMBUSTION_ACTIVITY_TYPE,
  ELECTRICITY_CONSUMPTION_ACTIVITY_TYPE,
] as const;

export const FACILITIES_ACTIVITY_TYPES = [
  BUILDING_ACTIVITY_TYPE,
  ...UTILITIES_ACTIVITY_TYPES,
] as const;
export type FacilitiesActivityType = (typeof FACILITIES_ACTIVITY_TYPES)[number];

export type UtilityTypeMetadata = {
  id: string;
  utilityType: BuildingUtilityType;
  fuelKind: GQStationaryCombustionFuelKind | null;
};

/**
 * This type was copied from a previous GraphQL fragment,
 * `BuildingFieldsForGridPage`, which was used throughout. We no longer actually
 * use it in GraphQL, so it's simpler to separate it out.
 */
export type BuildingFieldsType = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  buildingName: string;
  buildingUniqueId: string;
  streetAddress: string | null;
  streetAddress2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  buildingKind: string | null;
  buildingSubkind: string | null;
  startYearMonth: YearMonth | null;
  endYearMonth: YearMonth | null;
  leaseType: BuildingLeaseType | null;
  sizeUnit: BuildingSizeUnit | null;
  isWithoutNaturalGas: boolean | null;
  isWithoutRefrigerants: boolean | null;
  isEmpty: boolean | null;
  editState: BuildingEditState;
  hasAnyUtilities: boolean | null;
  periods: Array<BuildingPeriodType>;
  customFieldValues: Array<BuildingCustomFieldValueFields>;
  supportingDocuments: Array<GQBuildingFormSupportingDocumentFragment>;
  utilityTypesEntered: Array<UtilityTypeMetadata> | null;
  // CSRD E3 water fields needed to calculate if water activity
  // happens in areas of high water stress. #team-win-csrd
  latitude: number | null;
  longitude: number | null;
};

export type DeprecatedBuildingFields =
  | 'hasAnyUtilities'
  | 'utilityTypesEntered'
  | 'customFieldValues'
  | 'isEmpty';

export type BuildingPeriodType = {
  id: string;
  startYearMonth: YearMonth | null;
  endYearMonth: YearMonth | null;
  cleanPower: BuildingCleanPowerStatus | null;
  partialCleanPowerPercent: number | null;
  cleanPowerContractualInstrument: GQCleanPowerContractualInstrument | null;
  powerUsageEffectiveness: number | null;
  sizeQuantity: number | null;
  customFieldId: string | null;
  customFieldValue: string | null;
  orgUnitTypeStableId: string | null;
  orgUnitStableId: string | null;
  kind: BuildingPeriodKind;
  isBuildingEmpty: boolean | null;
};

export type BuildingCustomFieldValueFields = {
  id: string;
  startYearMonth: YearMonth | null;
  endYearMonth: YearMonth | null;
  value: string | null;
  field: {
    id: string;
    type: GQIngestionCustomFieldType;
    name: string;
  };
};

/**
 * In bulk settings, strictly-typed GraphQL types add a lot of performance
 * overhead. Buildings are particularly complex types with a lot of nested
 * fields, and it ends up being a huge improvement to use `UntypedData` for each.
 * We still split them out and ID each one, so that we get urql cache
 * invalidation.
 *
 * `UntypedBuildingDataContract` is the contract between the server and the client
 * - enforced by types on the server and assumed (via `as`) on the client.
 */
export type UntypedBuildingDataContract = Omit<
  BuildingFieldsType,
  | 'id'
  | 'customFieldValues'
  | 'supportingDocuments'
  | 'utilityTypesEntered'
  | 'createdAt'
  | 'updatedAt'
> & {
  customFieldValues: Array<
    Omit<BuildingCustomFieldValueFields, 'field'> & { field: { id: string } }
  >;
  supportingDocuments: Array<
    Omit<GQBuildingFormSupportingDocumentFragment, 'createdAt'> & {
      // if I had more time, I would fix this slightly better by not defining
      // this type in GraphQL. GraphQL has a scalarsExchange which makes this
      // type wrong.
      createdAt: string;
    }
  >;
  createdAt: string;
  updatedAt: string;
  utilityTypesEntered: Array<UtilityTypeMetadata> | null;
};

export type BuildingWithUtilitiesForGql = {
  id: string;
};

// TODO: Would be good to unify this with the very similar Zod enum in utilitiesSdiTypes.ts
const BuildingUtilityTypeZod = z.enum(
  getZodEnumValuesFromEnum(BuildingUtilityType)
);

export function validateBuildingUtilityString(
  value: string
): BuildingUtilityType {
  return BuildingUtilityTypeZod.parse(value);
}

export type BuildingCompletionInterval = {
  utilityType: BuildingUtilityType;
  fuelKind: GQStationaryCombustionFuelKind | null;
  periodsWithActuals?: Array<{ start: Date; end: Date }>;
  periodsWithSupportingDocs?: Array<{ start: Date; end: Date }>;
};

export type UntypedBuildingCompletionIntervalsContract = Pick<
  BuildingCompletionInterval,
  'utilityType' | 'fuelKind'
> & {
  periodsWithActuals?: Array<{ start: string; end: string }>;
  periodsWithSupportingDocs?: Array<{ start: string; end: string }>;
};

export const UTILITIES_ACTIVITY_TYPE_TO_BUILDING_UTILITY_TYPE: Record<
  UtilitiesActivityType,
  BuildingUtilityType
> = {
  [STATIONARY_COMBUSTION_ACTIVITY_TYPE]:
    BuildingUtilityType.StationaryCombustion,
  [ELECTRICITY_CONSUMPTION_ACTIVITY_TYPE]:
    BuildingUtilityType.ElectricityConsumption,
  [ELECTRICITY_CONSUMPTION_DATACENTER_ACTIVITY_TYPE]:
    BuildingUtilityType.ElectricityConsumption,
  [WASTE_ACTIVITY_TYPE]: BuildingUtilityType.Waste,
  [WATER_CONSUMPTION_ACTIVITY_TYPE]: BuildingUtilityType.Water,
  [WATER_USAGE_ACTIVITY_TYPE]: BuildingUtilityType.Water,
  [WATER_RECYCLING_ACTIVITY_TYPE]: BuildingUtilityType.Water,
  [WATER_STORED_ACTIVITY_TYPE]: BuildingUtilityType.Water,
  [WATER_DISCHARGE_ACTIVITY_TYPE]: BuildingUtilityType.Water,
  [REFRIGERANTS_ACTIVITY_TYPE]: BuildingUtilityType.Refrigerant,
};

export const BUILDING_UTILITY_TYPE_TO_UTILITIES_ACTIVITY_TYPES: Record<
  BuildingUtilityType,
  Array<UtilitiesActivityType>
> = {
  [BuildingUtilityType.StationaryCombustion]: [
    STATIONARY_COMBUSTION_ACTIVITY_TYPE,
  ],
  [BuildingUtilityType.ElectricityConsumption]: [
    ELECTRICITY_CONSUMPTION_ACTIVITY_TYPE,
    ELECTRICITY_CONSUMPTION_DATACENTER_ACTIVITY_TYPE,
  ],
  [BuildingUtilityType.Waste]: [WASTE_ACTIVITY_TYPE],
  [BuildingUtilityType.Water]: [
    WATER_CONSUMPTION_ACTIVITY_TYPE,
    WATER_USAGE_ACTIVITY_TYPE,
    WATER_RECYCLING_ACTIVITY_TYPE,
    WATER_STORED_ACTIVITY_TYPE,
    WATER_DISCHARGE_ACTIVITY_TYPE,
  ],
  [BuildingUtilityType.Refrigerant]: [REFRIGERANTS_ACTIVITY_TYPE],
};

export const BI_MEASURE_TO_UTILITY_TYPE: Partial<
  Record<BiSupportedMeasure['measure'], BuildingUtilityType>
> = {
  fuelQuantity: BuildingUtilityType.StationaryCombustion,
  electricityQuantity: BuildingUtilityType.ElectricityConsumption,
  waterUsageQuantity: BuildingUtilityType.Water,
  wasteQuantity: BuildingUtilityType.Waste,
};

export function isFacilityOutsideCoverageInterval(
  facility: {
    startYearMonth: YearMonth | null;
    endYearMonth: YearMonth | null;
  },
  coverageInterval: YMInterval
): {
  outsideOfInterval: boolean;
  reason:
    | 'facilityLeaseEndsBeforeInterval'
    | 'facilityLeaseStartsAfterInterval'
    | null;
} {
  if (
    facility.endYearMonth &&
    facility.endYearMonth <= coverageInterval.start
  ) {
    return {
      outsideOfInterval: true,
      reason: 'facilityLeaseEndsBeforeInterval',
    };
  }
  if (
    facility.startYearMonth &&
    facility.startYearMonth >= coverageInterval.end
  ) {
    return {
      outsideOfInterval: true,
      reason: 'facilityLeaseStartsAfterInterval',
    };
  }
  return { outsideOfInterval: false, reason: null };
}

/** Indicates we want to create a utility super-BART that spans all buildings. */
type SuperBartUtilityRowFilter = {
  type: 'SuperBart';
};

/** Indicates we want to create an empty placeholder for this activity type. */
type EmptyBartUtilityRowFilter = {
  type: 'EmptyBart';
};

/** Indicates we want to create a BART for a single user upload. */
type UserUploadUtilityRowFilter = {
  type: 'UserUpload';
  userUploadId: string;
  measurementProjectId: string;
};

type SuperBartBuildingFiilter = {
  type: 'SuperBart';
};

type MeasurementProjectBuildingFilter = {
  type: 'MeasurementProject';
  measurementProjectId: string;
};

export type UtilityRowFilter =
  // Old facilities
  | SuperBartUtilityRowFilter
  // New facilities with approvals
  | UserUploadUtilityRowFilter
  // Empty BART for this activity type
  | EmptyBartUtilityRowFilter;

export type BuildingFilter =
  // Old facilities
  | SuperBartBuildingFiilter
  // New facilities with approvals
  | MeasurementProjectBuildingFilter;
